import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faChevronDown, faTimes } from '@fortawesome/pro-light-svg-icons';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

interface WithdrawalsSelectionToolbarProps {
  selectedCount: number;
  markableCount: number;
  onClose: () => void;
  onMarkPaidBulk: () => void;
}

export const WithdrawalsSelectionToolbar = memo(
  ({ selectedCount, markableCount, onClose, onMarkPaidBulk }: WithdrawalsSelectionToolbarProps) => {
    const { t } = useTranslation('withdrawalsModule');
    const confirmModalRef = useRef<ConfirmModalRef>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    const translations = useMemo(
      () => ({
        selected: t('selection.selected'),
        markPaid: t('selection.markPaid'),
        actions: t('selection.actions'),
      }),
      [t],
    );

    const handleMarkPaid = useCallback(() => {
      if (markableCount > 0) {
        confirmModalRef.current?.open(
          () => onMarkPaidBulk(),
          undefined,
          t('confirmModals.bulkMarkPaid.title'),
          t('confirmModals.bulkMarkPaid.message', { count: markableCount }),
          t('buttons.confirm'),
          t('buttons.cancel'),
        );
      }
    }, [markableCount, onMarkPaidBulk, t]);

    if (selectedCount === 0) {
      return null;
    }

    return (
      <>
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
            <div className="flex items-center gap-4">
              {/* Contador de seleccionados - Selected count */}
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-btn-text text-sm rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
                <span className="text-sm font-medium">{translations.selected}</span>
              </div>

              {/* Acción bulk marcar como pagadas - Bulk mark paid action */}
              {!isMobile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkPaid}
                  disabled={markableCount === 0}
                  className={`border-card-border bg-transparent ${markableCount === 0 ? 'opacity-50' : ''}`}
                  data-testid="bulk-mark-paid-button"
                >
                  <FontAwesomeIcon icon={faCircleCheck} className="w-3 h-3 mr-1 text-success" />
                  {translations.markPaid} ({markableCount})
                </Button>
              )}

              {isMobile && (
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-card-border bg-transparent">
                      {translations.actions}
                      <FontAwesomeIcon icon={faChevronDown} className="ml-2 w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 border-card-border">
                    <DropdownMenuItem
                      onClick={handleMarkPaid}
                      disabled={markableCount === 0}
                      className={markableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={faCircleCheck} className="mr-2 w-3.5 text-success" />
                      {translations.markPaid} ({markableCount})
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-0 hover:bg-transparent"
                data-testid="clear-selection-button"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <ConfirmModal ref={confirmModalRef} />
      </>
    );
  },
);

WithdrawalsSelectionToolbar.displayName = 'WithdrawalsSelectionToolbar';
